/**
 * Choosing rows of another table to link to.
 *
 * The one editor that cannot live in a cell. It is a search box and a list of
 * what the search found, which is taller than a row and would be clipped by the
 * scroll container the grid draws it in -- so the field is marked as needing
 * room, and the grid answers a double-click on one of its cells by opening the
 * row rather than by opening an editor it has nowhere to put.
 *
 * **The draft is the linked rows themselves, as JSON.** Every other type
 * encodes its draft as something a person could have typed, because for every
 * other type a person could have. A link row is chosen, never typed, and what
 * is chosen has to carry both halves: the id, which is what the server is told,
 * and the primary field's text, which is what the cell renders before the
 * answer comes back. A draft of ids alone would leave the cell blank for as
 * long as the request took.
 */
import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, FieldOf } from '@/api/types'
import { cn } from '@/shared/lib/utils'
import { Input } from '@/shared/ui/input'
import type { CellEditorProps } from '.'
import { useEditorSources } from './sources'

/** One row of the related table, as a link row cell holds it. */
export interface LinkedRow {
  id: number
  value: string
}

/** How long to wait after a keystroke before asking. */
const SEARCH_DELAY = 150

/**
 * Whether the field will hold more than one link.
 *
 * The backend refuses a second one outright, so a picker that let somebody
 * choose two would be a picker whose every use failed.
 */
const allowsMany = (field: Field): boolean =>
  (field as FieldOf<'link_row'>).link_row_multiple_relationships !== false

/**
 * A list of links, keeping only the ones that name a row.
 *
 * A cell can hold a link to a row that has not been saved yet, which has no id
 * -- and a link with no id is one the server cannot be told about. The same
 * rule reads a stored value and a draft, because they are the same shape and a
 * second copy of it would be a second place for them to disagree.
 */
export function normaliseLinks(value: unknown): LinkedRow[] {
  if (!Array.isArray(value)) return []
  return value
    .filter(
      (link): link is LinkedRow => typeof (link as LinkedRow | null)?.id === 'number',
    )
    .map(({ id, value: name }) => ({
      id,
      value: typeof name === 'string' ? name : '',
    }))
}

/** The rows a draft names, or none when it says nothing readable. */
export function parseLinks(draft: string): LinkedRow[] {
  if (draft.trim() === '') return []
  try {
    return normaliseLinks(JSON.parse(draft))
  } catch {
    // A draft that is not this editor's own -- a character typed into the cell
    // before the field was known to need room, say. Nothing linked is the only
    // reading of it that is not a guess.
    return []
  }
}

export function LinkRowInput({
  field,
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps) {
  const { t } = useTranslation()
  const { linkCandidates } = useEditorSources()
  const chosen = parseLinks(value)
  const many = allowsMany(field)

  const [term, setTerm] = useState('')
  const [candidates, setCandidates] = useState<LinkedRow[]>([])
  const box = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let current = true
    const timer = setTimeout(() => {
      void linkCandidates(field, term)
        .then((rows) => {
          // The answer to a question nobody is asking any more: two more
          // characters have been typed since it was sent.
          if (current) setCandidates(rows)
        })
        .catch(() => {
          if (current) setCandidates([])
        })
    }, SEARCH_DELAY)

    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [linkCandidates, field, term])

  const write = (links: readonly LinkedRow[]) => {
    onValueChange(JSON.stringify(links))
  }

  const linked = new Set(chosen.map((link) => link.id))

  return (
    <div className="flex flex-col gap-2" onKeyDown={onKeyDown}>
      {chosen.length > 0 && (
        <ul className="flex flex-wrap gap-1" aria-label={t('link.linked')}>
          {chosen.map((link) => (
            <li key={link.id}>
              <button
                type="button"
                aria-label={t('link.unlink', { name: link.value })}
                onClick={() => {
                  write(chosen.filter((held) => held.id !== link.id))
                }}
                className="flex items-center gap-1 rounded-full border border-input px-2 py-0.5 text-xs hover:bg-muted/40"
              >
                {link.value === '' ? t('link.unnamed', { id: link.id }) : link.value}
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative">
        <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={box}
          autoFocus={autoFocus}
          aria-label={label}
          value={term}
          placeholder={t('link.search')}
          onChange={(event) => {
            setTerm(event.target.value)
          }}
          className="pl-8"
        />
      </div>

      <ul
        aria-label={t('link.candidates')}
        className="max-h-48 overflow-y-auto rounded border"
      >
        {candidates.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('link.nothingFound')}
          </li>
        )}
        {candidates.map((row) => {
          const name = row.value
          return (
            <li key={row.id}>
              <button
                type="button"
                aria-pressed={linked.has(row.id)}
                onClick={() => {
                  // Choosing one that is already linked takes it off, which is
                  // the only thing the second press of a control can sensibly
                  // mean and saves reaching for its chip.
                  //
                  // On a field that holds one link, choosing *replaces*. That
                  // is the opposite of what a paste of two names does, where
                  // the first wins -- and both are right: one is a choice made
                  // knowing what was there, the other is a list arriving in an
                  // order nobody chose.
                  write(
                    linked.has(row.id)
                      ? chosen.filter((held) => held.id !== row.id)
                      : many
                        ? [...chosen, { id: row.id, value: name }]
                        : [{ id: row.id, value: name }],
                  )
                }}
                className={cn(
                  'w-full px-2 py-1.5 text-left text-sm hover:bg-muted/40',
                  linked.has(row.id) && 'font-medium text-primary',
                )}
              >
                {name === '' ? t('link.unnamed', { id: row.id }) : name}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
